import React, { useState, useEffect } from 'react';
import { paymentsAPI, adminSettingsAPI } from '../services/api';
import { Booking, Provider } from '../types';
import { CreditCard, Shield, Check, AlertCircle } from 'lucide-react';

interface PaymentGatewayProps {
  booking: Booking;
  provider: Provider;
  onPaymentSuccess: (transactionId: string) => void;
  onPaymentError: (error: string) => void;
}

const PaymentGateway: React.FC<PaymentGatewayProps> = ({
  booking,
  provider,
  onPaymentSuccess,
  onPaymentError
}) => {
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');
  const [processing, setProcessing] = useState(false);
  const [paymentStep, setPaymentStep] = useState<'select' | 'processing' | 'success' | 'error'>('select');
  const [transactionId, setTransactionId] = useState('');
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState<any[]>([]);
  const [loadingMethods, setLoadingMethods] = useState(true);
  const [taxPercentage, setTaxPercentage] = useState<number | null>(null);

  // Fetch available payment methods and tax percentage on component mount
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        const response = await fetch('/payments/methods');
        const data = await response.json();
        if (data.success) {
          setAvailablePaymentMethods(data.data.paymentMethods);
          // Auto-select first available method
          if (data.data.paymentMethods.length > 0) {
            setSelectedPaymentMethod(data.data.paymentMethods[0].id);
          }
        }
      } catch (error) {
        console.error('Error fetching payment methods:', error);
        // Fallback to default methods if API fails
        setAvailablePaymentMethods([
          {
            id: 'cash_on_delivery',
            name: 'Cash on Delivery',
            description: 'Pay when service is completed',
            icon: '💰',
            supported: ['Cash Payment']
          }
        ]);
        setSelectedPaymentMethod('cash_on_delivery');
      } finally {
        setLoadingMethods(false);
      }
    };

    const fetchTaxPercentage = async () => {
      try {
        const response = await adminSettingsAPI.getSettings();
        if (response.success && response.data?.driverPricing?.taxPercentage !== undefined) {
          setTaxPercentage(response.data.driverPricing.taxPercentage);
        }
      } catch (error) {
        console.error('Error fetching tax percentage:', error);
      }
    };

    fetchPaymentMethods();
    fetchTaxPercentage();
  }, []);

  const calculateBookingDetails = () => {
    // If booking has pricingBreakdown (driver bookings), use that
    if (booking.pricingBreakdown) {
      return {
        duration: 0, // Not applicable for driver bookings
        hourlyRate: 0, // Not applicable for driver bookings
        subtotal: booking.pricingBreakdown.subtotal || 0,
        tax: booking.pricingBreakdown.tax || 0,
        total: booking.pricingBreakdown.total || booking.totalAmount || 0
      };
    }

    // For general service bookings, calculate from tripStartDateTime/tripEndDateTime and hourlyRate
    if (!booking.tripStartDateTime || !booking.tripEndDateTime) {
      return {
        duration: 0,
        hourlyRate: provider.hourlyRate || 0,
        subtotal: 0,
        tax: 0,
        total: booking.totalAmount || 0
      };
    }
    
    const startDateTime = new Date(booking.tripStartDateTime);
    const endDateTime = new Date(booking.tripEndDateTime);
    const durationMs = endDateTime.getTime() - startDateTime.getTime();
    const durationHours = durationMs / (1000 * 60 * 60);
    const subtotal = durationHours * provider.hourlyRate;
    
    // Calculate tax: use tax percentage from database if available, otherwise calculate from total
    let tax = 0;
    if (taxPercentage !== null) {
      tax = subtotal * (taxPercentage / 100);
    } else {
      // Last resort: use booking's totalAmount - subtotal if available
      tax = booking.totalAmount ? booking.totalAmount - subtotal : 0;
    }
    
    const total = subtotal + tax;

    return {
      duration: durationHours,
      hourlyRate: provider.hourlyRate,
      subtotal,
      tax,
      total
    };
  };

  const bookingDetails = calculateBookingDetails();

  const handlePayment = async () => {
    try {
      setProcessing(true);
      setPaymentStep('processing');

      // Create payment intent
      const paymentIntent = await paymentsAPI.createPaymentIntent({
        bookingId: booking.id || booking._id!,
        paymentMethod: selectedPaymentMethod,
        currency: 'INR'
      });

      if (selectedPaymentMethod === 'cash_on_delivery') {
        // For Cash on Delivery, no gateway processing needed
        setTransactionId(paymentIntent.data.transactionId);
        setPaymentStep('success');
        onPaymentSuccess(paymentIntent.data.transactionId);
      } else {
        // For online payment methods, simulate gateway processing
        await new Promise(resolve => setTimeout(resolve, 2000));

        // In a real implementation, you would:
        // 1. Open the payment gateway (Razorpay/Stripe/Cashfree) modal
        // 2. Handle the payment success/failure callbacks
        // 3. Verify the payment with your backend

        // For demo purposes, we'll simulate a successful payment
        const verificationResult = await paymentsAPI.verifyPayment({
          transactionId: paymentIntent.data.transactionId,
          gatewayTransactionId: paymentIntent.data.gatewayTransactionId,
          paymentGateway: selectedPaymentMethod as 'razorpay' | 'stripe' | 'cashfree'
        });

        setTransactionId(verificationResult.data.transactionId);
        setPaymentStep('success');
        onPaymentSuccess(verificationResult.data.transactionId);
      }

    } catch (error: any) {
      console.error('Payment failed:', error);
      setPaymentStep('error');
      onPaymentError(error.response?.data?.message || 'Payment failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  if (paymentStep === 'success') {
    return (
      <div className="text-center py-12">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
          <Check className="h-6 w-6 text-green-600" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Payment Successful!</h3>
        <p className="text-gray-600 mb-4">
          Your booking has been confirmed. You'll receive a confirmation email shortly.
        </p>
        <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-700">
          <p><strong>Transaction ID:</strong> {transactionId}</p>
          <p><strong>Amount Paid:</strong> ₹{bookingDetails.total.toFixed(2)}</p>
        </div>
      </div>
    );
  }

  if (paymentStep === 'error') {
    return (
      <div className="text-center py-12">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
          <AlertCircle className="h-6 w-6 text-red-600" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Payment Failed</h3>
        <p className="text-gray-600 mb-4">
          There was an issue processing your payment. Please try again.
        </p>
        <button
          onClick={() => setPaymentStep('select')}
          className="px-4 py-2 bg-black text-white rounded-md hover:bg-black700"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900">Payment Details</h3>

      {/* Booking Summary */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h4 className="font-medium text-gray-900 mb-4">Booking Summary</h4>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Service Provider:</span>
            <span className="font-medium">{provider.name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Date:</span>
            <span className="font-medium">{formatDate(booking.bookingDate)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Time:</span>
            <span className="font-medium">
              {booking.tripStartDateTime && booking.tripEndDateTime
                ? `${formatTime(new Date(booking.tripStartDateTime).toTimeString().slice(0, 5))} - ${formatTime(new Date(booking.tripEndDateTime).toTimeString().slice(0, 5))}`
                : 'N/A'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Duration:</span>
            <span className="font-medium">{bookingDetails.duration} hours</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Hourly Rate:</span>
            <span className="font-medium">₹{typeof bookingDetails.hourlyRate === 'number' ? bookingDetails.hourlyRate.toFixed(2) : bookingDetails.hourlyRate}/hour</span>
          </div>
          
          <div className="border-t pt-3 mt-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Subtotal:</span>
              <span>₹{bookingDetails.subtotal.toFixed(2)}</span>
            </div>
            {bookingDetails.tax !== undefined && bookingDetails.tax !== null && bookingDetails.tax > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">Tax:</span>
                <span>₹{bookingDetails.tax.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between font-semibold text-lg border-t pt-2 mt-2">
              <span>Total:</span>
              <span>₹{bookingDetails.total.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Method Selection */}
      <div>
        <h4 className="font-medium text-gray-900 mb-4">Select Payment Method</h4>
        {loadingMethods ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-black"></div>
          </div>
        ) : (
          <div className="space-y-3">
            {availablePaymentMethods.map((method) => (
            <div
              key={method.id}
              className={`relative p-4 border rounded-lg cursor-pointer transition-all ${
                selectedPaymentMethod === method.id
                  ? 'border-black bg-white'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => setSelectedPaymentMethod(method.id)}
            >
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 text-2xl">
                  {method.icon}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h5 className="font-medium text-gray-900">{method.name}</h5>
                    {selectedPaymentMethod === method.id && (
                      <Check className="h-5 w-5 text-black600" />
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{method.description}</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {method.supported.map((support: string, index: number) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                      >
                        {support}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
          </div>
        )}
      </div>

      {/* Security Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Shield className="h-5 w-5 text-blue-600 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-blue-800">Secure Payment</p>
            <p className="text-blue-600 mt-1">
              Your payment information is encrypted and secure. We never store your card details.
            </p>
          </div>
        </div>
      </div>

      {/* Payment Button */}
      <div className="flex space-x-4">
        <button
          onClick={handlePayment}
          disabled={processing || paymentStep === 'processing'}
          className={`flex-1 py-3 px-6 rounded-lg font-medium transition-all ${
            processing || paymentStep === 'processing'
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-black text-white hover:bg-black700'
          }`}
        >
          {paymentStep === 'processing' ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              {selectedPaymentMethod === 'cash_on_delivery' ? 'Confirming Booking...' : 'Processing Payment...'}
            </div>
          ) : (
            <div className="flex items-center justify-center">
              {selectedPaymentMethod === 'cash_on_delivery' ? (
                <>
                  <span className="mr-2">💰</span>
                  Confirm Booking (Pay ₹{bookingDetails.total.toFixed(2)} on Delivery)
                </>
              ) : (
                <>
                  <CreditCard className="h-5 w-5 mr-2" />
                  Pay ₹{bookingDetails.total.toFixed(2)}
                </>
              )}
            </div>
          )}
        </button>
      </div>
    </div>
  );
};

export default PaymentGateway; 